import React, { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { REVIEWS } from "../mock";
import Reveal from "./Reveal";

const GoogleIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const REVIEWS_LIST = [
  { name: "Michael Brunner", city: "Zürich", text: "Pünktlich, sauberes Fahrzeug und ein sehr freundlicher Chauffeur. Der Transfer vom Flughafen lief absolut reibungslos – gerne wieder!" },
  { name: "Sandra Keller", city: "Luzern", text: "Trotz verspätetem Flug hat der Fahrer gewartet, ganz ohne Aufpreis. Fixpreis wie versprochen. Sehr empfehlenswert." },
  { name: "Thomas Weber", city: "Bern", text: "Für unsere Geschäftsreisen buchen wir nur noch hier. Zuverlässig, diskret und immer auf die Minute pünktlich." },
  { name: "Laura Bianchi", city: "Lugano", text: "Wir sind mit zwei Kindern gereist – Kindersitze waren kostenlos vorbereitet. Sehr familienfreundlich und entspannt." },
  { name: "David Fischer", city: "St. Gallen", text: "Einfache Buchung, transparenter Preis und ein top gepflegter Mercedes. Genau so stelle ich mir Service vor." },
  { name: "Nadia Meier", city: "Davos", text: "Transfer nach Davos im Winter – souveräne Fahrweise auf verschneiten Strassen. Fühlte mich jederzeit sicher." },
];

const Reviews = () => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [ref, setRef] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name || rating === 0) {
      toast.error("Please add your name and a rating.");
      return;
    }
    toast.success("Thank you for your review!", {
      description: `${name} · ${rating}★`,
    });
    setName("");
    setComment("");
    setRef("");
    setRating(0);
  };

  return (
    <section id="reviews" className="bg-[#F5F2ED] border-y border-[#E9E5DE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <Reveal>
          <p className="font-body text-sm text-[#2B4BFF] font-semibold mb-3">{REVIEWS.kicker}</p>
          <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05] max-w-2xl">
            {REVIEWS.title}
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {REVIEWS_LIST.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 80} className="rounded-3xl bg-white border border-[#E9E5DE] p-6 elev-soft flex flex-col">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, s) => (<Star key={s} size={16} className="text-[#F5A623] fill-[#F5A623]" />))}
              </div>
              <p className="font-body text-[#3d4257] leading-relaxed flex-1">“{r.text}”</p>
              <div className="flex items-center gap-3 mt-5">
                <span className="w-10 h-10 rounded-full bg-[#EEF1FF] text-[#2B4BFF] font-heading font-semibold flex items-center justify-center">{r.name.charAt(0)}</span>
                <div>
                  <p className="font-heading font-semibold text-sm text-[#0B1533]">{r.name}</p>
                  <p className="font-body text-xs text-[#5A607A]">Zürich → {r.city}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <Reveal className="rounded-3xl bg-white border border-[#E9E5DE] p-8 flex flex-col justify-center elev-soft">
            <div className="flex items-center gap-2 mb-4">
              <GoogleIcon />
              <span className="font-heading font-semibold text-lg text-[#0B1533]">Google</span>
            </div>
            <div className="flex items-end gap-3">
              <span className="font-heading font-bold text-6xl tracking-tight text-[#0B1533]">
                {REVIEWS.rating}
              </span>
              <div className="pb-2">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-[#F5A623] fill-[#F5A623]" />
                  ))}
                </div>
                <p className="font-body text-sm text-[#5A607A] mt-1">{REVIEWS.count}</p>
              </div>
            </div>
            <p className="font-body text-sm text-[#5A607A] mt-6 leading-relaxed">{REVIEWS.promise}</p>
          </Reveal>

          <Reveal delay={100} className="lg:col-span-2">
            <form onSubmit={submit} className="rounded-3xl bg-white border border-[#E9E5DE] p-8 elev-soft">
              <h3 className="font-heading font-semibold text-xl tracking-tight text-[#0B1533]">
                Leave a review
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-[#F5F2ED] border border-transparent rounded-2xl px-4 py-3 font-body text-sm focus:border-[#2B4BFF] focus:bg-white focus:outline-none transition-colors duration-200"
                />
                <div className="flex items-center gap-3 px-1">
                  <span className="font-body text-sm text-[#5A607A]">Your rating</span>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        size={24}
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        className={`cursor-pointer transition-colors duration-150 ${
                          n <= (hover || rating)
                            ? "text-[#F5A623] fill-[#F5A623]"
                            : "text-[#D8D3CA]"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows="3"
                placeholder="Share your experience…"
                className="w-full mt-4 bg-[#F5F2ED] border border-transparent rounded-2xl px-4 py-3 font-body text-sm focus:border-[#2B4BFF] focus:bg-white focus:outline-none transition-colors duration-200"
              />
              <input
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                placeholder="Booking number"
                className="w-full mt-4 bg-[#F5F2ED] border border-transparent rounded-2xl px-4 py-3 font-body text-sm focus:border-[#2B4BFF] focus:bg-white focus:outline-none transition-colors duration-200"
              />
              <button
                type="submit"
                className="mt-4 inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors duration-200"
              >
                Submit review
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
